// v5.2 — LiveOverlayPage mobile-first : carte d'identité commerçant premium avec le
// masque AYA rotatif (identité visuelle des changements de page) + mini-carte produit
// épinglé dynamique. Fond transparent — superposition live / source navigateur OBS.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
const GOLD = "#E8C56E";
const GOLD_DARK = "#CDA452";
const GOLD_GRAD = "linear-gradient(135deg, #F5E9C6, #E4C87F 55%, #CDA452)";
const NAVY_GRAD = "linear-gradient(120deg, #12122E 0%, #1B1747 55%, #241A55 100%)";
const MASK_URL = "/aya/mask-small.png";

const fmtF = (n) => new Intl.NumberFormat("fr-FR").format(n).replace(/\u202f/g, ".");

// ── Icônes badges dorées (style bouclier, maquette) ─────────────────────────
const IconShieldLock = ({ s }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5l8-3z" />
    <rect x="9" y="10.5" width="6" height="5" rx="1" />
    <path d="M10.5 10.5V9a1.5 1.5 0 013 0v1.5" />
  </svg>
);
const IconScooter = ({ s }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="17.5" r="2.5" /><circle cx="18" cy="17.5" r="2.5" />
    <path d="M8.5 17.5h7M18 17.5l-2.5-8H12M13 6h2.5l1 3.5" />
  </svg>
);
const IconShieldCheck = ({ s }) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5l8-3z" />
    <path d="M8.5 12l2.5 2.5 4.5-5" />
  </svg>
);

export default function LiveOverlayPage() {
  const { sessionId } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [compact, setCompact] = useState(typeof window !== "undefined" ? window.innerWidth < 640 : true);

  useEffect(() => {
    const onResize = () => setCompact(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const st = document.createElement("style");
    st.id = "aya-overlay-style";
    st.textContent = `
      html, body, #root, .App { background: transparent !important; }
      @keyframes aya-ov-pop { from { opacity: 0; transform: translateY(10px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
    `;
    document.head.appendChild(st);
    return () => { st.remove(); };
  }, []);

  useEffect(() => {
    let iv;
    const load = async () => {
      try {
        const r = await fetch(`${API}/v3/live/sessions/${sessionId}/overlay-info`);
        const b = await r.json();
        if (!r.ok || !b.ok) throw new Error(b.error || "load_failed");
        setInfo(b.overlay);
      } catch { setError(true); }
    };
    load();
    iv = setInterval(load, 5000); // la mini-carte produit suit l'épinglage en quasi temps réel
    return () => clearInterval(iv);
  }, [sessionId]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (error) return <div style={{ color: "#fff", padding: 20, fontSize: 13 }}>Overlay indisponible — session introuvable.</div>;
  if (!info) return null;

  // Mobile-first : tailles compactes par défaut, élargies sur grand écran
  const S = (m, d) => (compact ? m : d);

  const rating = info.rating ? Number(info.rating).toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : null;
  const promo = info.flash_promo || null;
  const promoLeftMs = promo ? new Date(promo.ends_at).getTime() - now : 0;
  const promoActive = promo && promoLeftMs > 0;
  const mm = String(Math.floor(promoLeftMs / 60000)).padStart(2, "0");
  const ss = String(Math.max(0, Math.floor((promoLeftMs % 60000) / 1000))).padStart(2, "0");
  const pin = info.pinned_product || null;
  const pinPrice = pin && Number.isFinite(pin.price_fcfa) ? pin.price_fcfa : null;
  const pinPromoPrice = pinPrice != null && promoActive ? Math.round(pinPrice * (1 - promo.discount_pct / 100)) : null;
  const qrSize = S(62, 118);

  // QR premium — masque AYA centré (toujours sur la même ligne que l'identité)
  const qrBlock = (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: S(3, 6), flexShrink: 0 }}>
      <div style={{
        background: "#fff", borderRadius: S(10, 16), padding: S("4px 6px 6px", "8px 10px 10px"),
        border: `2px solid ${GOLD_DARK}`,
        boxShadow: `0 6px 20px rgba(205,164,82,0.4), 0 0 0 3px rgba(232,197,110,0.25)`,
        display: "flex", flexDirection: "column", alignItems: "center", gap: S(3, 6),
      }}>
        <div style={{ fontSize: S(6.5, 10.5), fontWeight: 900, letterSpacing: S(0.5, 1.4), color: "#17130A", textTransform: "uppercase", whiteSpace: "nowrap" }}>
          Scannez pour acheter
        </div>
        <div style={{ position: "relative", width: qrSize, height: qrSize }}>
          <img
            src={`${API.replace(/\/api$/, "")}${info.qr_url}`}
            alt="QR boutique AYA"
            width={qrSize} height={qrSize}
            style={{ borderRadius: S(6, 8), background: "#fff", display: "block" }}
            data-testid="live-overlay-qr"
          />
          {/* Masque AYA au centre du QR (error correction H côté serveur) */}
          <div style={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
            width: S(17, 32), height: S(17, 32), borderRadius: S(4, 7), background: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 0 2px #fff",
          }} data-testid="live-overlay-qr-logo">
            <img src={MASK_URL} alt="" style={{ width: S(10, 19), height: S(15, 28), objectFit: "contain" }} />
          </div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: S(4, 5), fontSize: S(7.5, 12), fontWeight: 800, whiteSpace: "nowrap" }}>
        <span style={{ width: S(5, 8), height: S(5, 8), borderRadius: "50%", background: "#EF4444", boxShadow: "0 0 8px rgba(239,68,68,0.9)", display: "inline-block" }} />
        Pendant le LIVE
      </div>
    </div>
  );

  return (
    <div style={{ padding: S(6, 10), fontFamily: "'Inter', system-ui, sans-serif", display: "flex", justifyContent: "center" }} data-testid="live-overlay-page">
      <div style={{ width: "100%", maxWidth: 960 }}>

        {/* ═══ CARTE D'IDENTITÉ COMMERÇANT — permanente pendant le live ═══ */}
        <div
          data-testid="live-overlay-banner"
          style={{
            width: "100%",
            background: NAVY_GRAD,
            border: `2px solid ${GOLD_DARK}`,
            borderRadius: S(14, 24),
            boxShadow: `0 10px 34px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08), 0 0 0 1px rgba(232,197,110,0.25)`,
            padding: S("7px 10px 6px", "14px 18px 12px"),
            color: "#F6F3EA",
            boxSizing: "border-box",
          }}
        >
          <div style={{ display: "flex", alignItems: "stretch", gap: S(7, 18) }}>
            {/* Masque AYA */}
            <div style={{ display: "flex", alignItems: "center", gap: S(5, 12), paddingRight: S(7, 18), borderRight: "1px solid rgba(232,197,110,0.3)" }}>
              <img
                src={MASK_URL} alt="AYA"
                style={{
                  width: S(26, 52), height: S(44, 88), objectFit: "contain",
                  filter: "drop-shadow(0 0 14px rgba(232,197,110,0.45))",
                }}
                data-testid="live-overlay-logo"
              />
              <div>
                <div style={{ fontSize: S(14, 30), fontWeight: 900, letterSpacing: S(1.2, 2), lineHeight: 1, color: "#fff" }}>AYA</div>
                <div style={{ fontSize: S(7, 11.5), color: GOLD, fontWeight: 700, marginTop: S(2, 3), whiteSpace: "nowrap" }}>Tout, pour tous.</div>
              </div>
            </div>

            {/* Boutique — nom DORÉ + ✓ + note (avis) ★★★★★ */}
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: S(0, 2) }}>
              <div style={{ fontSize: S(7.5, 13), fontWeight: 800, letterSpacing: S(1.3, 2.6), color: GOLD, textTransform: "uppercase" }}>Boutique</div>
              <div style={{ display: "flex", alignItems: "center", gap: S(4, 9), minWidth: 0 }}>
                <span data-testid="live-overlay-name" style={{
                  fontSize: S(String(info.merchant_name || "").length > 16 ? 11 : 13, 29), fontWeight: 900, letterSpacing: 0.3, textTransform: "uppercase",
                  background: GOLD_GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                  lineHeight: 1.12, overflow: "hidden",
                  ...(compact
                    ? { display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }
                    : { whiteSpace: "nowrap", textOverflow: "ellipsis" }),
                  filter: "drop-shadow(0 1px 6px rgba(232,197,110,0.25))",
                }}>
                  {info.merchant_name}
                </span>
                {info.verified && (
                  <span aria-label="Vérifié" style={{
                    width: S(12, 23), height: S(12, 23), borderRadius: "50%", background: GOLD_GRAD, color: "#3A2E10",
                    display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: S(7, 12.5), fontWeight: 900, flexShrink: 0,
                    boxShadow: "0 2px 8px rgba(205,164,82,0.5)",
                  }}>✓</span>
                )}
              </div>
              {(info.tagline || info.category) && (
                <div style={{ fontSize: S(9, 14.5), color: "rgba(246,243,234,0.9)", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {info.tagline || info.category}
                </div>
              )}
              {rating && (
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: S(3.5, 9), marginTop: S(3, 6),
                  border: "1.5px solid rgba(232,197,110,0.5)", borderRadius: S(7, 12),
                  padding: S("1.5px 6px", "5px 14px"), alignSelf: "flex-start", background: "rgba(0,0,0,0.3)",
                  maxWidth: "100%",
                }}>
                  <span style={{ color: GOLD, fontSize: S(8.5, 17) }}>★</span>
                  <span style={{ fontSize: S(10.5, 21), fontWeight: 900 }}>{rating}</span>
                  {Number.isFinite(info.reviews_count) && info.reviews_count > 0 && (
                    <span style={{ fontSize: S(7.5, 12.5), color: "rgba(246,243,234,0.75)", borderLeft: "1px solid rgba(232,197,110,0.4)", paddingLeft: S(4, 10), fontWeight: 700, whiteSpace: "nowrap" }}>
                      ({fmtF(info.reviews_count)} avis)
                    </span>
                  )}
                  {!compact && <span style={{ color: GOLD, letterSpacing: 2, fontSize: 12 }}>★★★★★</span>}
                </div>
              )}
            </div>

            {/* QR — même ligne que l'identité, sur tous les écrans */}
            {qrBlock}
          </div>

          {/* Promo flash — compte à rebours */}
          {promoActive && (
            <div data-testid="live-overlay-promo" style={{
              display: "flex", alignItems: "center", gap: S(8, 12), marginTop: S(8, 12),
              background: GOLD_GRAD, borderRadius: S(11, 14), padding: S("7px 10px", "10px 16px"), color: "#2A2108",
            }}>
              <span style={{ fontSize: S(15, 20) }} aria-hidden="true">⚡</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: S(10.5, 14), fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  Promo flash · {promo.label} · −{promo.discount_pct}%
                </div>
                <div style={{ fontSize: S(8, 10.5), fontWeight: 700, opacity: 0.8 }}>Uniquement pendant le live</div>
              </div>
              <div data-testid="live-overlay-promo-countdown" style={{
                fontSize: S(16, 24), fontWeight: 900, fontFeatureSettings: "'tnum'", letterSpacing: 1,
                background: "rgba(20,16,4,0.85)", color: "#F5E9C6", borderRadius: 10, padding: S("3px 9px", "4px 12px"),
              }}>
                {mm}:{ss}
              </div>
            </div>
          )}

          {/* Badges dorés — une seule ligne */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: compact ? "space-between" : "flex-start",
            gap: S(6, 26), marginTop: S(6, 11), paddingTop: S(6, 11),
            borderTop: "1px solid rgba(232,197,110,0.25)",
          }}>
            {[[IconShieldLock, "Paiement sécurisé"], [IconScooter, "Livraison rapide"], [IconShieldCheck, "Commerçant vérifié"]].map(([Ic, label]) => (
              <div key={label} style={{ display: "flex", alignItems: "center", gap: S(3.5, 8), fontSize: S(7.5, 12.5), fontWeight: 800, letterSpacing: S(0.2, 0.5), textTransform: "uppercase", color: "rgba(246,243,234,0.95)", whiteSpace: "nowrap" }}>
                <Ic s={S(11, 17)} /> {label}
              </div>
            ))}
            {info.live_title && !compact && (
              <div style={{ marginLeft: "auto", fontSize: 12, color: "rgba(246,243,234,0.7)", fontStyle: "italic", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {info.live_title}
              </div>
            )}
          </div>
        </div>

        {/* ═══ MINI-CARTE PRODUIT ÉPINGLÉ — change avec la présentation ═══ */}
        {pin && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: S(10, 14), paddingRight: 4 }}>
            <div key={pin.product_id} data-testid="live-overlay-pinned" style={{
              width: S(140, 236), background: "#fff", borderRadius: S(13, 18), overflow: "hidden",
              boxShadow: "0 14px 40px rgba(0,0,0,0.45)", color: "#17181C",
              animation: "aya-ov-pop 0.45s cubic-bezier(0.2, 0.9, 0.3, 1.2)",
            }}>
              <div style={{ position: "relative", width: "100%", aspectRatio: "1 / 0.92", background: "#F3F4F6" }}>
                {pin.photo ? (
                  <img src={pin.photo} alt={pin.name} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 46 }}>🛍</div>
                )}
                <span style={{
                  position: "absolute", top: S(7, 10), left: S(7, 10),
                  background: "#F43F7E", color: "#fff", fontSize: S(9, 11), fontWeight: 800,
                  borderRadius: 999, padding: S("3px 8px", "4px 11px"), display: "inline-flex", alignItems: "center", gap: 4,
                  boxShadow: "0 3px 10px rgba(244,63,126,0.45)",
                }} data-testid="live-overlay-pinned-badge">
                  🏷 En vente
                </span>
                {pinPromoPrice != null && (
                  <span style={{
                    position: "absolute", top: S(7, 10), right: S(7, 10),
                    background: "#17181C", color: "#F5E9C6", fontSize: S(9, 11), fontWeight: 900,
                    borderRadius: 999, padding: S("3px 7px", "4px 10px"),
                  }}>−{promo.discount_pct}%</span>
                )}
              </div>
              <div style={{ padding: S("8px 10px 10px", "11px 13px 13px") }}>
                <div data-testid="live-overlay-pinned-name" style={{ fontSize: S(12, 15), fontWeight: 700, lineHeight: 1.25, marginBottom: S(3, 5), display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {pin.name}
                </div>
                {pinPrice != null && (
                  pinPromoPrice != null ? (
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
                      <div style={{ fontSize: S(15.5, 20), fontWeight: 900, fontFeatureSettings: "'tnum'" }}>{fmtF(pinPromoPrice)} FCFA</div>
                      <div style={{ fontSize: S(10.5, 12.5), color: "#9CA3AF", textDecoration: "line-through", fontFeatureSettings: "'tnum'" }}>{fmtF(pinPrice)} F</div>
                    </div>
                  ) : (
                    <div style={{ fontSize: S(15.5, 20), fontWeight: 900, fontFeatureSettings: "'tnum'" }}>{fmtF(pinPrice)} FCFA</div>
                  )
                )}
                <div data-testid="live-overlay-pinned-buy" style={{
                  marginTop: S(7, 10), background: "#F43F7E", color: "#fff", textAlign: "center",
                  fontSize: S(12, 14.5), fontWeight: 800, borderRadius: S(10, 12), padding: S("7px 0", "9px 0"),
                  boxShadow: "0 6px 16px rgba(244,63,126,0.35)",
                }}>
                  Acheter
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
